import { useMemo } from "react";
import {
  filter,
  has,
  includes,
  intersection,
  reduce,
  reject,
  size,
} from "lodash";
import { Facet } from "@linkfluence/models/facet";
import { useFacetsFromContext } from "@radarly/core/facets/use-cases";
import { Clause } from "@radarly/models/clause";
import {
  getNotClause,
  isCustomFieldsClause,
  isTermClause,
} from "@radarly/models/clause/is";

export function extractClauseWithoutIncludedFocuses(clause: Clause) {
  const clauses = reject(clause ? clause.and : [], (innerClause) => {
    return isTermClause(innerClause) && has(innerClause, [Facet.Focuses]);
  });
  return { and: clauses };
}

export function getFocusesFromClause(clause: Clause) {
  if (clause && clause.and) {
    return reduce(
      clause.and,
      (acc, innerClause) => {
        if (isTermClause(innerClause) && has(innerClause, Facet.Focuses)) {
          if (size(acc)) {
            return intersection(acc, innerClause[Facet.Focuses]);
          } else {
            return innerClause[Facet.Focuses];
          }
        } else {
          return acc;
        }
      },
      []
    );
  } else {
    return [];
  }
}

export function getExcludedFocusesFromClause(clause: Clause) {
  if (clause && clause.and) {
    const not = getNotClause(clause)?.not;
    if (not && not[Facet.Focuses]) {
      return not[Facet.Focuses];
    }
  }
  return [];
}

export function getFocusesOccurencesFromClause(clause: Clause) {
  if (clause && clause.and) {
    return reduce(
      clause.and,
      (acc, innerClause) => {
        if (isTermClause(innerClause) && has(innerClause, Facet.Focuses)) {
          acc.push(innerClause[Facet.Focuses]);
        }
        return acc;
      },
      []
    );
  } else {
    return [];
  }
}

function getCustomFieldsValuesFromClause(clause: Clause, term: string) {
  if (clause && clause.and) {
    return reduce(
      clause.and,
      (acc, innerClause) => {
        if (
          isCustomFieldsClause(innerClause) &&
          has(innerClause.customFields, term)
        ) {
          if (size(acc)) {
            return intersection(acc, innerClause.customFields[term]);
          } else {
            return innerClause.customFields[term];
          }
        } else {
          return acc;
        }
      },
      []
    );
  } else {
    return [];
  }
}

export function removeCustomFieldsFromClause(clause: Clause): Clause {
  if (clause && clause.and) {
    return {
      and: clause.and.filter(
        (innerClause) => !isCustomFieldsClause(innerClause)
      ),
    };
  }
  return { and: [] };
}

export function useFacets(term: Facet, clause: Clause) {
  const facets = useFacetsFromContext();
  const facet = facets[term];
  return useMemo(() => {
    if (term === Facet.Focuses) {
      const filteredFocuses = getFocusesFromClause(clause);
      if (size(filteredFocuses)) {
        return {
          ...facet,
          values: filter(facet?.values, (value) =>
            includes(filteredFocuses, value.id)
          ),
        };
      } else {
        return facet;
      }
    } else if (
      facet &&
      facet.custom &&
      facet.type === "closed" &&
      facet.mode === "multi"
    ) {
      const filteredValues = getCustomFieldsValuesFromClause(clause, facet.id);
      if (size(filteredValues)) {
        return {
          ...facet,
          values: filter(facet.values, (value) =>
            includes(filteredValues, value.id)
          ),
        };
      } else {
        return facet;
      }
    } else {
      return facet;
    }
  }, [clause, facet, term]);
}
